import type { JSX } from 'react';
import { EXAMPLE_HOUSE } from '../examples.js';
import { BrickWall, FolderOpen, Grid3x3, House, PenLine } from 'lucide-react';

/**
 * Design document C1. The generator changes what "getting started" means, so
 * an empty wall offers four routes rather than a single hint line.
 */
export function EmptyState({
  run,
  onDismiss,
}: {
  readonly run: (id: string) => void;
  readonly onDismiss: () => void;
}): JSX.Element {
  // Choosing a route has to get the card out of the way: it sits over the
  // drawing surface, so leaving it up would block the very action just chosen.
  const choose = (id: string) => () => {
    onDismiss();
    run(id);
  };
  return (
    <div className="emptystate">
      <div className="card">
        <h2>Start a wall</h2>
        <p>
          A building is drawn one wall at a time, arranged into a plan, then looked at in 3D — the
          three steps along the top. This is the first. Everything is at true size in millimetres,
          with the mortar joints enforced as you go.
        </p>
        <div className="routes">
          <button onClick={choose('sketch.rectangle')}>
            <Grid3x3 size={18} aria-hidden />
            <span>
              <b>Draw a wall and fill it</b>
              <span>Drag a rectangle, pick a bond, and lay the whole wall at once.</span>
            </span>
          </button>
          <button onClick={choose('tool.sketch')}>
            <PenLine size={18} aria-hidden />
            <span>
              <b>Sketch an outline</b>
              <span>Draw any shape, mark windows and doors, then fill or lay it.</span>
            </span>
          </button>
          <button onClick={choose('tool.build')}>
            <BrickWall size={18} aria-hidden />
            <span>
              <b>Lay bricks by hand</b>
              <span>Choose a unit and click. The preview snaps to real joint positions.</span>
            </span>
          </button>
          <button onClick={choose('file.example')}>
            <House size={18} aria-hidden />
            <span>
              <b>Open the example house</b>
              <span>{EXAMPLE_HOUSE.summary}</span>
            </span>
          </button>
          <button onClick={choose('file.open')}>
            <FolderOpen size={18} aria-hidden />
            <span>
              <b>Open a project</b>
              <span>A .bwall project, or a .brickwall.json wall from the desktop tool.</span>
            </span>
          </button>
        </div>
        <button className="dismiss" onClick={onDismiss} aria-label="Dismiss">
          Dismiss
        </button>
        <p className="hint">
          P pan · M measure · wheel zoom · Space+drag pan · R rotate · [ ] change wall · Ctrl+K
          commands
        </p>
      </div>
    </div>
  );
}
